import { useEffect, useState } from "react";
import { useTheme } from "../../../components/hooks/useTheme";
import { GridColDef } from "@mui/x-data-grid";
import { Divider, Menu, MenuItem, Stack } from "@mui/material";
import { CustomDataGrid } from "../../../components/common/CustomDataGrid";
import baseURL from "../../../configs/api";
import { EditDamage } from "./EditDamage";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";
import useGeneral from "../../../components/hooks/useGeneral";
import moment from "moment";

type DamageListProps = {
    paginationModel: any;
    setPaginationModel: (value: any) => void;
    searchValue: string;
    setSearchValue: (value: string) => void;
    damageFilterValue: number;
    setDamageFilterValue: (value: number) => void;
    selectedFilter: string;
    setSelectedFilter: (value: string) => void;
    filterItems: any[];
    idNumber: any;
    setRefreshDamageList: (value: boolean) => void;
}

export const DamageList = ({
    paginationModel,
    setPaginationModel,
    searchValue,
    setSearchValue,
    damageFilterValue,
    setDamageFilterValue,
    selectedFilter,
    setSelectedFilter,
    filterItems,
    idNumber,
    setRefreshDamageList
}: DamageListProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const [loading, setLoading] = useState(true);
    const [searchTableData, setSearchTableData] = useState<any[]>([]);
    const [totalLength, setTotalLength] = useState<number>(0);
    const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [openEditDamage, setOpenEditDamage] = useState<boolean>(false);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);
    const columnFilterOpen = Boolean(columnFilterAnchorEl);
    const [damageId, setDamageId] = useState<number>(0);
    const initialColumns: GridColDef[] = [
        {
            field: "id",
            headerName: "#",
            width: 100,
            editable: false,
            sortable: false,
            align: "left",
        },
        {
            field: "dateOfAccident",
            headerName: "Date of Accident",
            width: 300,
            align: "left",
            editable: false,
            sortable: false,
            renderCell: (params) => {
                return (
                    <span
                        style={{ color: "var(--app-link-color)" }}
                        className="underline cursor-pointer"
                        onClick={() => {
                            setDamageId(params?.row?.id);
                            setOpenEditDamage(true);
                        }}
                    >
                        {params?.value}
                    </span>
                );
            },
        },
        {
            field: "accidentLocation",
            headerName: "Accident Location",
            width: 300,
            align: "left",
            editable: false,
            sortable: false,
        },
        {
            field: "reservationId",
            headerName: "Reservation",
            width: 300,
            align: "left",
            editable: false,
            sortable: false,
        },
        {
            field: "customerName",
            headerName: "Customer Name",
            align: "left",
            width: 150,
            editable: false,
            sortable: false,
        },
        {
            field: "driverName",
            headerName: "Driver Name",
            align: "left",
            editable: false,
            sortable: false,
            width: 300,
        },
        {
            field: "createdAt",
            headerName: "Created At",
            align: "left",
            width: 300,
            editable: false,
            sortable: false,
        },
        {
            field: "createdBy",
            headerName: "Created By",
            align: "left",
            width: 300,
            editable: false,
            sortable: false,
        },
    ];
    const [columns, setColumns] = useState<GridColDef[]>(initialColumns);
    const [selectedColumns, setSelectedColumns] = useState(initialColumns?.map(col => col?.headerName));

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setColumnFilterAnchorEl(event.currentTarget);
    };
    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const toggleColumn = (headerName: string) => {
        setSelectedColumns(prev =>
            prev.includes(headerName) ? prev.filter(col => col !== headerName) : [...prev, headerName]
        );
    };

    useEffect(() => {
        const filterCol = initialColumns?.filter(col => selectedColumns?.includes(col?.headerName));
        setColumns(filterCol);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedColumns])

    const toggleAll = () => {
        if (selectedColumns?.length === initialColumns?.length) {
            setSelectedColumns([]);
        } else {
            setSelectedColumns(initialColumns?.map(col => col?.headerName));
        }
    };

    useEffect(() => {
        if (searchValue === "" && !isFirstTime) {
            const newPaginationModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
            };

            setPaginationModel(newPaginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue === "") {
            setIsFirstTime(false);
            emptySearchApiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const emptySearchApiCall = (newPaginationModel: any) => {

        setLoading(true);
        fetch(`${baseURL}damageReport/GetDamageReportsNamingPagingByVehicleId/${idNumber?.state},${newPaginationModel.page},${newPaginationModel.pageSize}`, {
            headers: {
                'X-Tenant-ID': tenantId,
            }
        })
            .then((res) => res.json())
            .then((data) => {
                const result = data.damageReports.map((item: any) => {
                    return {
                        ...item,
                        dateOfAccident: moment(new Date(item?.dateOfAccident)).format(dateFormat + " hh:mm a"),
                        createdAt: moment(new Date(item?.createdAt)).format(dateFormat + " hh:mm a"),
                    };
                });

                setSearchTableData(result);
                setTotalLength(data.count);
                setLoading(false);
                handleTenantError(null);
            })
            .catch((error) => {
                handleTenantError(error);
                setSearchTableData([]);
                setLoading(false);
            });

    }

    useEffect(() => {
        let timeOutId: NodeJS.Timeout;

        if (searchValue !== "") {
            timeOutId = setTimeout(() => {
                const newPaginationModel = {
                    page: 0,
                    pageSize: paginationModel?.pageSize,
                };

                setPaginationModel(newPaginationModel);
            }, 1000);
        }

        return () => clearTimeout(timeOutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    useEffect(() => {
        if (searchValue !== "") {
            apiCall(paginationModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel])

    const apiCall = (newPaginationModel: any) => {
        let filterData: any[] = [];
        setLoading(true);

        switch (damageFilterValue) {
            case 0:
                fetch(`${baseURL}damageReport/GetDamageReportsNamingPagingByVehicleIdAndSearchWithCustomerName?customerName=${searchValue}&vehicleId=${idNumber?.state}&page=${newPaginationModel.page}&size=${newPaginationModel.pageSize}`, {
                    headers: {
                        'X-Tenant-ID': tenantId,
                    }
                })
                    .then((res) => res.json())
                    .then((data) => {

                        filterData = data.damageReports.map((item: any) => {
                            return {
                                ...item,
                                dateOfAccident: moment(new Date(item?.dateOfAccident)).format(dateFormat + " hh:mm a"),
                                createdAt: moment(new Date(item?.createdAt)).format(dateFormat + " hh:mm a"),
                            };
                        });

                        setSearchTableData(filterData);
                        setTotalLength(data.count);
                        setLoading(false);
                        handleTenantError(null);
                    })
                    .catch((error) => {
                        handleTenantError(error);
                        setSearchTableData([]);
                        setLoading(false);
                    });
                break;
        }
    }


    return (
        <>
            <div className="w-full mt-2">
                <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
                    <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>
                        <div className="flex items-center gap-1.5">

                            <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Damages</h2>

                            <div >
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                                    aria-controls={columnFilterOpen ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={columnFilterOpen ? 'true' : undefined}
                                    onClick={handleColumnFilterClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>Columns</span>
                                        <span className={`material-symbols-outlined text-[18px] font-light ${themeStyles.main.textHeadingColor}`}>
                                            filter_alt
                                        </span>
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={columnFilterAnchorEl}
                                    open={columnFilterOpen}
                                    onClose={handleColumnFilterClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 170,
                                                maxHeight: 300,
                                                backgroundColor: themeStyles.topBar.bg
                                            }
                                        }
                                    }}
                                >
                                    <div className="flex items-center gap-2 px-[16px] py-[6px] w-full">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox cursor-pointer"
                                            checked={selectedColumns?.length === initialColumns?.length ? true : false}
                                            onClick={toggleAll}
                                        />
                                        <span className={`${themeStyles.topBar.tailwindTextColor} font-medium text-[12px]`}>Select All</span>
                                    </div>
                                    <Divider className="w-full" />
                                    {
                                        initialColumns?.map((column, index) => (
                                            <>
                                                <MenuItem
                                                    key={index}
                                                    sx={{
                                                        fontSize: "12px",
                                                        marginBlock: "0px",
                                                        "&: hover": {
                                                            cursor: "default"
                                                        }
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            className="form-checkbox cursor-pointer"
                                                            checked={selectedColumns.includes(column?.headerName)}
                                                            onChange={() => toggleColumn(column?.headerName || "")}
                                                        />
                                                        <span className={`${themeStyles.topBar.tailwindTextColor} font-medium`}>{column?.headerName}</span>
                                                    </div>

                                                </MenuItem>
                                                <Divider className="w-full" />
                                            </>

                                        ))
                                    }
                                </Menu>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div >
                                <button
                                    className={`flex items-center justify-between py-[5px] px-[12px] gap-2.5 rounded-lg border border-solid ${themeStyles.topBar.tailwindBorderColor}`}
                                    aria-controls={open ? 'basic-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    onClick={handleClick}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <span className={`${themeStyles.topBar.tailwindTextColor} text-[14px] font-medium`}>{selectedFilter}</span>
                                        {open ? <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                                            keyboard_arrow_up
                                        </span> : <span className={`material-symbols-outlined ${themeStyles.topBar.tailwindTextColor} text-[25px] font-extralight`}>
                                            keyboard_arrow_down
                                        </span>}
                                    </Stack>
                                </button>

                                <Menu
                                    id="basic-menu"
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleClose}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 170,
                                                backgroundColor: themeStyles.topBar.bg
                                            }
                                        }
                                    }}
                                >
                                    {
                                        filterItems.map((filter: any, index: number) => (
                                            <>
                                                <MenuItem
                                                    key={index}
                                                    sx={{ fontSize: "12px", marginBlock: "0px" }}
                                                    onClick={() => {
                                                        setSelectedFilter(filter.label)
                                                        setDamageFilterValue(filter.id)
                                                        setAnchorEl(null);

                                                    }}>
                                                    <span className={`${themeStyles.topBar.tailwindTextColor} text-[16px] font-medium`}>{filter.label}</span>
                                                </MenuItem>
                                                <Divider className="w-full" />
                                            </>
                                        ))

                                    }
                                </Menu>
                            </div>
                            <div className={`flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border ${themeStyles.main.borderColor} shadow-sm`}>
                                <span className={`material-symbols-outlined text-[25px] font-light ${themeStyles.main.textSubHeadingColor}`}>
                                    search
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchValue}
                                    className={`border-none outline-none w-full ${themeStyles.main.textSubHeadingColor} bg-transparent`}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <CustomDataGrid
                        filteredRows={searchTableData}
                        columns={columns?.map(col => ({
                            ...col,
                            headerClassName: themeStyles.table.headerBg,
                        }))}
                        loading={loading}
                        totalLength={totalLength}
                        paginationModel={paginationModel}
                        setPaginationModel={setPaginationModel}
                    />
                </div>
            </div>

            {
                openEditDamage && (
                    <EditDamage
                        idNumber={idNumber}
                        openEditDamage={openEditDamage}
                        setOpenEditDamage={setOpenEditDamage}
                        setRefreshDamageList={setRefreshDamageList}
                        damageId={damageId}
                    />
                )
            }

        </>

    )
}